/*
 * datalayer.js – DOM event listeners dla GA4 eventów
 * DL.push() i DL.buildItem() są zdefiniowane w theme.liquid (inline, bez defer)
 * żeby były dostępne dla skryptów inline w sekcjach Liquid.
 */

// ─── Event listeners (po załadowaniu DOM) ─────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {

  // Helper: parse JSON z atrybutu data-dl-item
  const parseItem = el => {
    const raw = el?.dataset?.dlItem;
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };

  // Helper: kontekst listy (ustawiany przez strony kolekcji/search/slidery)
  const listCtx = () => window.__dlListCtx ?? { id: '', name: '' };

  // Helper: komponent koszyka zależny od kontekstu strony
  const cartComp = () => window.__dlGlobals?.page_type === 'cart' ? 'cart_page' : 'cart_drawer';

  // Stan siatki: typ ostatniej akcji (filter/sort) + dedup + dedup sortu
  let _lastGridAction = 'filter';
  let _lastSortValue = null;
  let _lastViewItemListKey = null;

  // ── 1. select_item ───────────────────────────────────────────────────────────
  document.addEventListener('click', e => {
    const link = e.target.closest('a[data-product-link]');
    if (!link) return;
    const card = link.closest('product-card');
    if (!card) return;
    const data = parseItem(card);
    if (!data) return;

    if (!data.item_list_id) {
      const ctx = listCtx();
      data.item_list_id   = ctx.id;
      data.item_list_name = ctx.name;
    }

    // P-10: rzeczywista pozycja w siatce produktów (po filtrach/sortowaniu)
    const gridCards = [...document.querySelectorAll('#product-grid product-card')];
    const domIndex = gridCards.indexOf(card);
    if (domIndex !== -1) data.index = domIndex;

    DL.push('select_item', {
      component:    'product_card',
      component_id: data.item_list_id || '',
      ecommerce: {
        item_list_id:   data.item_list_id   || '',
        item_list_name: data.item_list_name || '',
        items: [DL.buildItem(data)]
      }
    });
  });

  // ── 2. add_to_cart (karta produktu – single-variant form) ──────────────────
  document.addEventListener('submit', e => {
    const form = e.target;
    if (!form.classList.contains('product-card__add-to-cart--form')) return;
    const card = form.closest('product-card');
    if (!card) return;
    const data = parseItem(card);
    if (!data) return;

    DL.push('add_to_cart', {
      component: 'product_card',
      ecommerce: {
        currency: window.__dlGlobals?.currency ?? '',
        value:    parseFloat(data.price) || 0,
        items:    [DL.buildItem({ ...data, quantity: 1 })]
      }
    });
  }, true);

  // ── 3. add_to_cart (PDP product-form) ───────────────────────────────────────
  document.addEventListener('submit', e => {
    const form = e.target;
    if (form.dataset.type !== 'add-to-cart-form') return;
    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput) return;
    const variantId = parseInt(variantInput.value, 10);
    const variants  = window.__dlPdpVariants;
    if (!variants?.[variantId]) return;
    const data = variants[variantId];
    const qty  = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;

    // Inject DL data as hidden line item properties (_prefix hides from cart UI)
    // These are read in pixel.js checkout events via customAttributes
    const propMap = {
      '_dl_color':   data.item_color,
      '_dl_size':    data.item_size,
      '_dl_den':     data.item_den,
      '_dl_cat':     data.item_category,
      '_dl_cat2':    data.item_category2,
      '_dl_badge':   data.item_badge,
      '_dl_pattern': data.item_pattern,
      '_dl_promo':   data.item_is_promo,
      '_dl_best':    data.item_is_bestseller,
      '_dl_low30':   data.item_lowest_price_30d
    };
    for (const [key, val] of Object.entries(propMap)) {
      if (val == null || val === '' || val === false) continue;
      const inp = document.createElement('input');
      inp.type  = 'hidden';
      inp.name  = `properties[${key}]`;
      inp.value = String(val);
      form.appendChild(inp);
    }

    DL.push('add_to_cart', {
      component: 'add_to_cart',
      ecommerce: {
        currency: window.__dlGlobals?.currency ?? '',
        value:    (parseFloat(data.price) || 0) * qty,
        items:    [DL.buildItem({ ...data, quantity: qty })]
      }
    });
  }, true);

  // ── 4. remove_from_cart / add_to_cart (zmiana ilości w koszyku) ─────────────
  document.addEventListener('change', e => {
    const input = e.target;
    if (input.name !== 'updates[]') return;
    const oldQty = parseInt(input.getAttribute('value'), 10) || 0;
    const newQty = parseInt(input.value, 10) || 0;
    if (oldQty === newQty) return;

    const lineItem = input.closest('.cart-item');
    if (!lineItem) return;
    const data = parseItem(lineItem);
    if (!data) return;

    const diff     = Math.abs(newQty - oldQty);
    const isRemove = newQty < oldQty;

    DL.push(isRemove ? 'remove_from_cart' : 'add_to_cart', {
      component: cartComp(),
      ecommerce: {
        currency: window.__dlGlobals?.currency ?? '',
        value:    (parseFloat(data.price) || 0) * diff,
        items:    [DL.buildItem({ ...data, quantity: diff })]
      }
    });
  }, true);

  // ── 4b. remove_from_cart (przycisk kosza – cart-remove-button) ───────────
  document.addEventListener('click', e => {
    const btn = e.target.closest('cart-remove-button');
    if (!btn) return;
    const lineItem = btn.closest('.cart-item');
    if (!lineItem) return;
    const data = parseItem(lineItem);
    if (!data) return;
    const qty = parseInt(data.quantity, 10) || 1;

    DL.push('remove_from_cart', {
      component: cartComp(),
      ecommerce: {
        currency: window.__dlGlobals?.currency ?? '',
        value:    (parseFloat(data.price) || 0) * qty,
        items:    [DL.buildItem({ ...data, quantity: qty })]
      }
    });
  }, true);

  // ── 5. select_variant (PDP – zmiana wariantu) ────────────────────────────────
  if (window.subscribe) {
    let lastVariantId = null;
    subscribe('variant-change', event => {
      const variant = event?.data?.variant;
      if (!variant) return;
      // pomiń duplikaty (product-variants.js odpala event 2× przy inicjalizacji)
      if (variant.id === lastVariantId) return;
      lastVariantId = variant.id;
      const variants = window.__dlPdpVariants;
      if (variants?.[variant.id]) {
        window.__dlPdpItem = variants[variant.id];
      }
      DL.push('select_variant', {
        page_type:     'pdp',
        component:     'variant_picker',
        item_id:       String(variant.id),
        item_name:     variants?.[variant.id]?.item_name ?? '',
        variant_color: variant.option1 || '',
        variant_size:  variant.option2 || ''
      });
    });
  }

  // ── 6. view_cart (cart-drawer – obserwuje klasę is-visible) ──────────────────
  {
    const fetchAndPushViewCart = () => {
      // Primary: read cart items from DOM (data-dl-item has full schema incl. categories)
      const domCartItems = document.querySelectorAll('.cart-item[data-dl-item]');
      if (domCartItems.length) {
        const items = [];
        domCartItems.forEach(el => {
          try { items.push(DL.buildItem(JSON.parse(el.dataset.dlItem))); } catch {}
        });
        if (items.length) {
          const totalEl    = document.querySelector('[data-cart-total-price]');
          const totalValue = totalEl ? parseInt(totalEl.dataset.cartTotalPrice, 10) / 100 : 0;
          DL.push('view_cart', {
            component: 'cart_drawer',
            ecommerce: { currency: window.__dlGlobals?.currency ?? '', value: totalValue, items }
          });
          return;
        }
      }
      // Fallback: fetch /cart.js (e.g. first open before DOM renders)
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          if (!cart?.items?.length) return;
          const items = cart.items.map(item => DL.buildItem({
            item_id:           item.sku || String(item.variant_id),
            item_name:         item.product_title,
            price:             item.final_price / 100,
            discount:          (item.original_price - item.final_price) / 100,
            quantity:          item.quantity,
            item_variant:      item.variant_title?.toLowerCase(),
            item_color:        item.variant_options?.[0]?.toLowerCase(),
            item_size:         item.variant_options?.[1]?.toLowerCase(),
            item_is_promo:     (item.compare_at_price || 0) > item.original_price,
            item_stock_status: 'in_stock'
          }));
          DL.push('view_cart', {
            component: 'cart_drawer',
            ecommerce: {
              currency: window.__dlGlobals?.currency ?? cart.currency,
              value:    cart.total_price / 100,
              items
            }
          });
        })
        .catch(() => {});
    };

    const cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) {
      new MutationObserver(mutations => {
        for (const m of mutations) {
          if (m.attributeName !== 'class') continue;
          const wasVisible = m.oldValue?.includes('is-visible');
          const isVisible  = cartDrawerEl.classList.contains('is-visible');
          if (!wasVisible && isVisible) fetchAndPushViewCart();
        }
      }).observe(cartDrawerEl, {
        attributes:        true,
        attributeOldValue: true,
        attributeFilter:   ['class']
      });
    }
  }

  // ── 7. begin_checkout ────────────────────────────────────────────────────────
  document.addEventListener('click', e => {
    const btn = e.target.closest('[name="checkout"]');
    if (!btn) return;
    // Preferuj __dlCartItems (strona /cart) lub czytaj z DOM cart-drawera
    let cartItems = window.__dlCartItems || [];
    if (!cartItems.length) {
      document.querySelectorAll('.cart-item[data-dl-item]').forEach(el => {
        try { cartItems.push(DL.buildItem(JSON.parse(el.dataset.dlItem))); } catch {}
      });
    }
    const totalEl    = document.querySelector('[data-cart-total-price]');
    const totalValue = totalEl ? parseInt(totalEl.dataset.cartTotalPrice, 10) / 100 : 0;
    DL.push('begin_checkout', {
      component: 'go_to_checkout',
      ecommerce: {
        currency: window.__dlGlobals?.currency ?? '',
        value:    totalValue,
        items:    cartItems
      }
    });
  });

  // ── 8. filter_open ───────────────────────────────────────────────────────────
  // MenuDrawer (global.js) ustawia atrybut "open" przed bubbling do document,
  // więc sprawdzamy details.open === true (czyli drawer właśnie się otworzył)
  document.addEventListener('click', e => {
    const summary = e.target.closest('summary.facets__button-filters');
    if (!summary) return;
    const details = summary.closest('details');
    if (!details?.open) return;
    DL.push('filter_open', { component: 'filters_drawer', category_name: listCtx().name });
  });

  // ── 9. filter_change ─────────────────────────────────────────────────────────
  document.addEventListener('change', e => {
    const input = e.target;
    if (!input.closest('facet-filters-form')) return;
    if (input.name === 'sort_by') return;
    if (input.type !== 'checkbox' && input.type !== 'radio') return;

    _lastGridAction = 'filter';

    // P-05: czytelne wartości zamiast surowych ID Shopify
    const labelEl = document.querySelector(`label[for="${input.id}"]`) || input.closest('label');
    const rawLabel = labelEl?.textContent?.replace(/\s+/g, ' ').trim() || input.value;
    const readableValue = rawLabel.replace(/\s*\(\d+\)\s*$/, '').trim();
    const cleanGroup = input.name
      .replace(/^filter\.p\.tag\./, 'tag_')
      .replace(/^filter\.p\.m\./, '')
      .replace(/^filter\.p\.option\./, '')
      .replace(/^filter\.v\.option\./, '')
      .replace(/^filter\.v\./, '');

    DL.push('filter_change', {
      component:     'filters_drawer',
      category_name: listCtx().name,
      filter_group:  cleanGroup,
      filter_value:  readableValue,
      filter_action: input.checked ? 'select' : 'deselect'
    });
  });

  // ── 10. filter_apply ─────────────────────────────────────────────────────────
  document.addEventListener('click', e => {
    const btn = e.target.closest('.js-btn-close-drawer');
    if (!btn?.closest('facet-filters-form')) return;
    const checked = document.querySelectorAll('facet-filters-form input[type="checkbox"]:checked');
    DL.push('filter_apply', {
      component:              'filters_drawer',
      category_name:          listCtx().name,
      filters_selected_count: checked.length,
      results_count:          document.querySelectorAll('#product-grid [data-dl-item]').length
    });
  });

  // ── 11. filter_clear (przycisk „wyczyść wszystko") ──────────────────────────
  document.addEventListener('click', e => {
    const btn = e.target.closest('.active-facets__button-remove, .facets__clear-button');
    if (!btn) return;
    DL.push('filter_clear', { component: 'filters_drawer', category_name: listCtx().name });
  });

  // ── 11b. filter_clean (usunięcie pojedynczego aktywnego filtra ✕) ──────────
  document.addEventListener('click', e => {
    const link = e.target.closest('facet-remove a, .active-facets__button-link');
    if (!link) return;
    const chip      = link.closest('.active-facets__button');
    const input     = chip?.querySelector('input[name]');
    const labelSpan = chip?.querySelector('.active-facets__button-text > span');
    DL.push('filter_clean', {
      component:    'filters_drawer',
      filter_group: input?.name ?? '',
      filter_value: labelSpan ? labelSpan.textContent.trim() : (input?.value ?? '')
    });
  });

  // ── 12. sort_change ──────────────────────────────────────────────────────────
  // Słuchamy click na opcjach custom-select (isTrusted = true), nie change na <select>
  // (change jest dispatchowany programatycznie zarówno przez custom-select jak i facets.js przy filtrach)
  document.addEventListener('click', e => {
    const option = e.target.closest('.js-btn-option[data-value]');
    if (!option) return;
    if (!option.closest('facet-filters-form')) return;
    if (!option.closest('[id*="SortBy"]')) return;
    const value = option.dataset.value;
    if (value === _lastSortValue) return;
    _lastSortValue = value;
    _lastGridAction = 'sort';
    DL.push('sort_change', {
      component:     'sort_dropdown',
      category_name: listCtx().name,
      sort_option:   value
    });
  });

  // ── 13. search (submit z drawera/nagłówka) ───────────────────────────────────
  document.addEventListener('submit', e => {
    const form = e.target;
    if (!form.closest('predictive-search')) return;
    const searchInput = form.querySelector('[name="q"]');
    if (!searchInput) return;
    DL.push('search', {
      component:   'search_bar',
      search_term: searchInput.value
    });
  }, true);

  // ── 14. view_size_chart (klik "Tabela rozmiarów" na PDP) ────────────────────
  document.addEventListener('click', e => {
    const btn = e.target.closest('.size-guide');
    if (!btn) return;
    const item = window.__dlPdpItem || {};
    DL.push('view_size_chart', {
      page_type: 'pdp',
      component: 'size_chart',
      item_id:   item.item_id   ? String(item.item_id)  : '',
      item_name: item.item_name || ''
    });
  });

  // ── 15. select_content (mega menu – linki nawigacyjne) ──────────────────────
  // closest() obsługuje kliknięcie w child (np. <span> wewnątrz <a>)
  document.addEventListener('click', e => {
    const link = e.target.closest('.header__nav-link, .header__nav-dropdown-link');
    if (!link) return;
    const href = link.getAttribute('href') || '';
    // pomiń linki-otwieracze dropdown (href="#" lub puste) – one nie nawigują
    if (!href || href === '#' || href.startsWith('javascript')) return;
    const isTopLevel = link.classList.contains('header__nav-link');
    // pobierz etykietę z bezpośredniego tekstu lub data-menu-item rodzica
    const liParent  = link.closest('[data-menu-item], [data-child-menu-item]');
    const menuLabel = liParent?.dataset.menuItem || liParent?.dataset.childMenuItem || link.textContent.trim();
    DL.push('select_content', {
      component:    'mega_menu',
      content_type: 'navigation_category',
      content_id:   href.replace(/^https?:\/\/[^/]+/, ''),
      menu_label:   menuLabel.trim(),
      menu_level:   isTopLevel ? 1 : 2
    });
  });

  // ── 16. newsletter_popup_view / sign_up / newsletter_popup_dismiss ──────────
  {
    const nlPopup = document.querySelector('newsletter-popup');
    if (nlPopup) {
      const nlId = nlPopup.id || 'newsletter_popup';

      // view – popup staje się widoczny (gubi klasę newsletter-popup-is-hidden)
      let wasHidden = nlPopup.classList.contains('newsletter-popup-is-hidden');
      new MutationObserver(mutations => {
        for (const m of mutations) {
          if (m.attributeName !== 'class') continue;
          const isHidden = nlPopup.classList.contains('newsletter-popup-is-hidden');
          if (wasHidden && !isHidden) {
            DL.push('newsletter_popup_view', {
              component: 'newsletter_popup', component_id: nlId
            });
          }
          wasHidden = isHidden;
        }
      }).observe(nlPopup, { attributes: true });

      // sign_up – submit formularza
      nlPopup.querySelector('form')?.addEventListener('submit', () => {
        DL.push('sign_up', {
          method: 'newsletter_popup', component: 'newsletter_popup', component_id: nlId
        });
      }, true);

      // dismiss – zamknięcie popupu
      nlPopup.querySelector('.js-button-close')?.addEventListener('click', () => {
        DL.push('newsletter_popup_dismiss', {
          component: 'newsletter_popup', component_id: nlId
        });
      });
    }
  }

  // ── 17. view_ugc_section (sekcja product-videos na PDP) ────────────────────
  // Odpala RAZ gdy sekcja z filmami wejdzie w viewport (IntersectionObserver).
  {
    const videosWrap = document.querySelector('.product-videos');
    if (videosWrap) {
      // Liczymy elementy UGC już przy inicjalizacji (DOM jest już załadowany)
      const ugcCount = videosWrap.querySelectorAll('[data-product-videos-item]').length;
      let fired = false;
      new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (fired || !entry.isIntersecting) continue;
          fired = true;
          const pdpItem = window.__dlPdpItem || {};
          DL.push('view_ugc_section', {
            page_type:        'pdp',
            component:        'ugc_gallery',
            item_id:          pdpItem.item_id   ? String(pdpItem.item_id)  : '',
            item_name:        pdpItem.item_name || '',
            ugc_assets_count: ugcCount
          });
        }
      }, { threshold: 0.1 }).observe(videosWrap);
    }
  }

  // ── 18. view_item_list po filtrach / sorcie (collection-grid-updated) ────────
  document.addEventListener('collection-grid-updated', () => {
    const ctx   = window.__dlListCtx || {};
    const cards = document.querySelectorAll('#product-grid [data-dl-item]');
    if (!cards.length) return;
    const items = [];
    cards.forEach((card, i) => {
      try {
        const data = JSON.parse(card.dataset.dlItem);
        items.push(DL.buildItem({
          ...data,
          index:          i,
          item_list_id:   ctx.id   || '',
          item_list_name: ctx.name || ''
        }));
      } catch {}
    });
    if (!items.length) return;

    // P-07: pomiń jeśli lista produktów identyczna jak poprzednia
    const key = items.map(it => it.item_id).join(',');
    if (key === _lastViewItemListKey) return;
    _lastViewItemListKey = key;

    DL.push('view_item_list', {
      component:        'product_grid',
      list_update_type: _lastGridAction,  // P-09: 'sort' lub 'filter'
      ecommerce: {
        item_list_id:   ctx.id   || '',
        item_list_name: ctx.name || '',
        items
      }
    });
  });

  // ── 19. view_promotion (hero banner – pierwsze wejście slajdu w viewport) ──
  {
    document.querySelectorAll('[data-dl-promo]').forEach(slide => {
      let fired = false;
      new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (fired || !entry.isIntersecting) continue;
          fired = true;
          let promo;
          try { promo = JSON.parse(slide.dataset.dlPromo); } catch { promo = {}; }
          DL.push('view_promotion', {
            component:    'hero_banner',
            component_id: promo.section_id || '',
            ecommerce: {
              items: [{
                item_id:        promo.promotion_id   || '',
                item_name:      promo.promotion_name || '',
                promotion_id:   promo.promotion_id   || '',
                promotion_name: promo.promotion_name || '',
                creative_name:  promo.creative_name  ?? '',
                creative_slot:  promo.creative_slot  !== undefined ? String(promo.creative_slot) : ''
              }]
            }
          });
        }
      }, { threshold: 0.5 }).observe(slide);
    });
  }

  // ── 20. select_promotion (klik w CTA banera hero) ───────────────────────────
  document.addEventListener('click', e => {
    const link = e.target.closest('[data-dl-promo] a, [data-dl-promo] button');
    if (!link) return;
    const slide = link.closest('[data-dl-promo]');
    if (!slide) return;
    let promo;
    try { promo = JSON.parse(slide.dataset.dlPromo); } catch { promo = {}; }
    DL.push('select_promotion', {
      component:    'hero_banner',
      component_id: promo.section_id || '',
      ecommerce: {
        items: [{
          item_id:        promo.promotion_id   || '',
          item_name:      promo.promotion_name || '',
          promotion_id:   promo.promotion_id   || '',
          promotion_name: promo.promotion_name || '',
          creative_name:  promo.creative_name  || 'hero_banner',
          creative_slot:  promo.creative_slot  !== undefined ? String(promo.creative_slot) : ''
        }]
      }
    });
  });

  // ── 21. category_list_view (lista kolekcji – pierwsze wejście gridu w viewport)
  {
    if (window.__dlGlobals?.page_type === 'category_list') {
      const collGrid = document.querySelector('#collection-grid');
      if (collGrid) {
        let fired = false;
        new IntersectionObserver(entries => {
          for (const entry of entries) {
            if (fired || !entry.isIntersecting) continue;
            fired = true;
            DL.push('category_list_view', {
              component:         'collection_grid',
              collections_count: collGrid.querySelectorAll('.card-collection').length
            });
          }
        }, { threshold: 0.1 }).observe(collGrid);
      }
    }
  }

  // ── 22. select_content (klik w kafelek kolekcji) ────────────────────────────
  document.addEventListener('click', e => {
    const link = e.target.closest('.card-collection__content-wrapper');
    if (!link) return;
    const card    = link.closest('.card-collection');
    const heading = card?.querySelector('.card-collection__heading span');
    DL.push('select_content', {
      component:    'collection_grid',
      content_type: 'collection_tile',
      content_id:   (link.getAttribute('href') || '').replace(/^https?:\/\/[^/]+/, ''),
      content_name: heading?.textContent.trim() ?? ''
    });
  });

  // ── 23. Edrone newsletter popup (iframe) ─────────────────────────────────────
  // Popup lives in #edrone-onsite-popup-iframe (cross-origin).
  // (a) MutationObserver: container appears in DOM → newsletter_popup_view
  // (b) postMessage from Edrone iframe → sign_up / newsletter_popup_dismiss
  // (c) MutationObserver fallback: container hides without sign_up → dismiss
  {
    let edroneViewFired   = false;
    let edroneSignUpFired = false;
    const EDRONE_ID       = 'edrone-onsite-popup-iframe';
    const COMPONENT_ID    = 'edrone_popup';

    const pushEdrone = (eventName, extra) =>
      DL.push(eventName, { component: 'newsletter_popup', component_id: COMPONENT_ID, ...extra });

    // (b) postMessage from Edrone iframe (subscribe / close actions)
    window.addEventListener('message', e => {
      const d = e.data;
      if (!d || typeof d !== 'object') return;

      const isSubscribe = d.type === 'edrone_subscribe'
        || d.edrone_event === 'subscribe'
        || (d.edrone === true && d.action === 'subscribe');
      const isClose = d.type === 'edrone_close'
        || d.edrone_event === 'close'
        || (d.edrone === true && d.action === 'close');

      if (isSubscribe && !edroneSignUpFired) {
        edroneSignUpFired = true;
        pushEdrone('sign_up', { method: 'newsletter_popup' });
      }
      if (isClose && !edroneSignUpFired) {
        pushEdrone('newsletter_popup_dismiss', {});
      }
    });

    // (a) Watch for the iframe container becoming visible (popup shown)
    const showObserver = new MutationObserver(() => {
      if (edroneViewFired) return;
      const iframe = document.getElementById(EDRONE_ID);
      if (!iframe) return;
      const container = iframe.closest('[class*="h-popup"]') || iframe.parentElement;
      if (!container) return;
      const s = window.getComputedStyle(container);
      if (s.display === 'none' || s.visibility === 'hidden') return;

      edroneViewFired = true;
      pushEdrone('newsletter_popup_view', {});
      showObserver.disconnect();

      // (c) Fallback dismiss: popup container hides/removed without sign_up event
      const dismissObserver = new MutationObserver(() => {
        const el   = document.getElementById(EDRONE_ID);
        const wrap = el ? (el.closest('[class*="h-popup"]') || el.parentElement) : null;
        const hidden = !wrap
          || window.getComputedStyle(wrap).display === 'none'
          || window.getComputedStyle(wrap).visibility === 'hidden';
        if (!hidden) return;
        if (!edroneSignUpFired) {
          pushEdrone('newsletter_popup_dismiss', {});
        }
        dismissObserver.disconnect();
      });
      dismissObserver.observe(document.body, {
        attributes:      true,
        attributeFilter: ['style', 'class'],
        childList:       true,
        subtree:         true
      });
    });
    showObserver.observe(document.body, { childList: true, subtree: true });
  }

  // ── 24. product_recommendations – view_item_list (C-03) & select_item (C-04) ─
  {
    const REC_LIST_ID   = 'product_recommendations';
    const REC_LIST_NAME = 'Product Recommendations';

    const fireRecViewItemList = recArea => {
      const items = [];
      recArea.querySelectorAll('[data-dl-item]').forEach((el, i) => {
        try {
          const d = JSON.parse(el.dataset.dlItem);
          items.push(DL.buildItem({ ...d, index: i, item_list_id: REC_LIST_ID, item_list_name: REC_LIST_NAME }));
        } catch {}
      });
      if (!items.length) return;
      DL.push('view_item_list', {
        component: 'product_recommendations',
        ecommerce: { item_list_id: REC_LIST_ID, item_list_name: REC_LIST_NAME, items }
      });
    };

    document.querySelectorAll('product-recommendations').forEach(wrapper => {
      const recArea = wrapper.querySelector('[data-recommendations]');
      if (!recArea) return;
      new MutationObserver((_, obs) => {
        if (!recArea.querySelector('[data-dl-item]')) return;
        obs.disconnect();
        fireRecViewItemList(recArea);
      }).observe(recArea, { childList: true });
    });

    document.addEventListener('click', e => {
      const btn = e.target.closest('.product-recommendations__item-button');
      if (!btn) return;
      const itemEl = btn.closest('[data-dl-item]');
      if (!itemEl) return;
      let d;
      try { d = JSON.parse(itemEl.dataset.dlItem); } catch { return; }
      DL.push('select_item', {
        component: 'product_recommendations',
        ecommerce: {
          item_list_id:   REC_LIST_ID,
          item_list_name: REC_LIST_NAME,
          items: [DL.buildItem({ ...d, item_list_id: REC_LIST_ID, item_list_name: REC_LIST_NAME })]
        }
      });
    });
  }

  // ── 25. shopify-forms-embed popup (newsletter) – H-01/H-02 ──────────────────
  // Popup żyje w shadow DOM (<shopify-forms-embed> z open shadow root).
  // Widoczność kontrolowana przez data-current-step na [data-form-root].
  // submit event nie przechodzi przez shadow boundary → śledzimy zmianę atrybutu.
  {
    let sfeViewFired   = false;
    let sfeSignUpFired = false;
    let sfeLastStep    = 'form';

    const SFE_COMP    = 'newsletter_popup';
    const SFE_COMP_ID = 'shopify_forms';

    const initShopifyForms = host => {
      const sr = host.shadowRoot;
      if (!sr) return;

      new MutationObserver(() => {
        const stepEl = sr.querySelector('[data-current-step]');
        if (!stepEl) return;

        // view: pierwsza obserwacja elementu formularza
        if (!sfeViewFired) {
          sfeViewFired = true;
          DL.push('newsletter_popup_view', { component: SFE_COMP, component_id: SFE_COMP_ID });
        }

        // sign_up: krok zmienił się z "form" na cokolwiek innego (sukces)
        const step = stepEl.dataset.currentStep;
        if (step && step !== sfeLastStep) {
          sfeLastStep = step;
          if (step !== 'form' && !sfeSignUpFired) {
            sfeSignUpFired = true;
            DL.push('sign_up', {
              method: 'newsletter_popup', component: SFE_COMP, component_id: SFE_COMP_ID
            });
          }
        }
      }).observe(sr, {
        childList: true, subtree: true,
        attributes: true, attributeFilter: ['data-current-step']
      });
    };

    // dismiss: klik w przycisk zamknięcia przechodzi przez shadow DOM (click jest composed)
    document.addEventListener('click', e => {
      if (!sfeViewFired || sfeSignUpFired) return;
      const path = e.composedPath ? e.composedPath() : [];
      const inForms = path.some(n => n?.tagName?.toLowerCase() === 'shopify-forms-embed');
      if (!inForms) return;
      const isClose = path.some(n =>
        n?.getAttribute?.('aria-label')?.match(/close|zamknij/i) ||
        (n?.classList && [...n.classList].some(c => /[Cc]lose|[Dd]ismiss/.test(c)))
      );
      if (isClose) {
        DL.push('newsletter_popup_dismiss', { component: SFE_COMP, component_id: SFE_COMP_ID });
      }
    });

    const hostEl = document.querySelector('shopify-forms-embed');
    if (hostEl) {
      initShopifyForms(hostEl);
    } else {
      new MutationObserver((_, obs) => {
        const el = document.querySelector('shopify-forms-embed');
        if (!el) return;
        obs.disconnect();
        initShopifyForms(el);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

});
